import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus, Eye, Ban, Undo2 } from "lucide-react";
import axiosInstance from "../../axiosInstance";
import SummaryCard from "../../components/ui/SummaryCard";
import StatusBadge, { normalizeStatus } from "../../components/ui/StatusBadge";
import {
  StatusTabs,
  SelectDropdown,
  DateRangeFilter,
  FilterToolbar,
} from "../../components/ui/FilterControls";
import { Truncate } from "../../components/ui/DataTable";
import GeneralTable from "../../components/Table/GeneralTable";
import ActionMenu from "../../components/ui/ActionMenu";
import DetailsDrawer, { DetailList } from "../../components/ui/DetailsDrawer";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import { ErrorState } from "../../components/ui/Feedback";
import {
  formatDates,
  formatDateLong,
  formatDuration,
  sortedLeaveDates,
  firstLeaveTimestamp,
  canRevokeLeave,
  leaveInDateRange,
} from "./leaveUtils";
import "./MyLeave.scss";

const LEAVE_API = `${process.env.REACT_APP_API_EMPLOYEE_URL}/leave-history`;

// "cancelled" also covers revoked leave (the backend status is "Revoke").
const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "cancelled", label: "Cancelled" },
];

const CONFIRM_COPY = {
  cancel: {
    title: "Cancel this leave request?",
    message:
      "The request will be withdrawn and your approver will no longer see it as pending. This can't be undone.",
    confirmLabel: "Cancel request",
    success: "Leave request cancelled",
  },
  revoke: {
    title: "Revoke this approved leave?",
    message:
      "The approved leave will be revoked and the days will no longer be reserved for you.",
    confirmLabel: "Revoke leave",
    success: "Leave revoked",
  },
};

const statusKeyOf = (leave) => normalizeStatus(leave.leaveStatus).key;

const MyLeave = () => {
  // --- data ---------------------------------------------------------------
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- filters ------------------------------------------------------------
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // --- UI state -----------------------------------------------------------
  const [selectedId, setSelectedId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type, leave }
  const [actionBusy, setActionBusy] = useState(false);

  // --- fetching -----------------------------------------------------------
  // `silent` is used after an action: if the refresh fails we keep the data
  // already on screen and only toast, instead of replacing the table with an
  // error state.
  const fetchLeaves = useCallback(async ({ silent = false } = {}) => {
    try {
      const response = await axiosInstance.get(`${LEAVE_API}/All`);
      setLeaveRequests(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching leave history:", err.message || err);
      if (silent) {
        toast.error("Couldn't refresh your leave list.");
      } else {
        setError(err.message || "An error occurred");
      }
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchLeaves().finally(() => setLoading(false));
  }, [fetchLeaves]);

  const handleRetry = () => {
    setLoading(true);
    fetchLeaves().finally(() => setLoading(false));
  };

  // --- cancel / revoke ----------------------------------------------------
  const runConfirmedAction = async () => {
    if (!confirm) return;
    const { type, leave } = confirm;
    setActionBusy(true);
    try {
      const response = await axiosInstance.put(
        `${LEAVE_API}/${type}/${leave.leaveId}`,
      );
      if (response.status !== 200) {
        throw new Error(`Failed to ${type} leave. Status: ${response.status}`);
      }
      toast.success(CONFIRM_COPY[type].success);
      setConfirm(null);
      await fetchLeaves({ silent: true });
    } catch (err) {
      console.error(`Error trying to ${type} leave:`, err);
      toast.error(err.message || `Failed to ${type} leave`);
    } finally {
      setActionBusy(false);
    }
  };

  // --- derived data -------------------------------------------------------
  const leaveTypeOptions = useMemo(() => {
    const names = Array.from(
      new Set(leaveRequests.map((l) => l.leaveTypeName).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
    return [
      { value: "all", label: "All Leave Types" },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [leaveRequests]);

  // Leave type + date range define the "period" that the summary cards and
  // status-tab counts describe. Status tab and free-text search then narrow
  // what the table shows. Free-text search is handled by GeneralTable.
  const periodRows = useMemo(
    () =>
      leaveRequests.filter(
        (leave) =>
          (typeFilter === "all" || leave.leaveTypeName === typeFilter) &&
          leaveInDateRange(leave.leaveDates, dateRange.from, dateRange.to),
      ),
    [leaveRequests, typeFilter, dateRange],
  );

  const counts = useMemo(() => {
    const result = {
      all: periodRows.length,
      pending: 0,
      approved: 0,
      rejected: 0,
      cancelled: 0,
      totalDays: 0,
    };
    periodRows.forEach((leave) => {
      result.totalDays += leave.noOfDays || 0;
      const key = statusKeyOf(leave);
      if (key in result) result[key] += 1;
    });
    return result;
  }, [periodRows]);

  const visibleRows = useMemo(
    () =>
      periodRows
        .filter(
          (leave) =>
            statusFilter === "all" || statusKeyOf(leave) === statusFilter,
        )
        .map((leave) => ({
          ...leave,
          searchableText: [
            leave.leaveTypeName,
            leave.comments,
            leave.responsibility,
            leave.fallBackEmployeeName,
            formatDuration(leave.leaveDuration),
            formatDates(leave.leaveDates),
          ]
            .filter(Boolean)
            .join(" "),
        })),
    [periodRows, statusFilter],
  );

  const filtersActive =
    statusFilter !== "all" ||
    typeFilter !== "all" ||
    Boolean(dateRange.from) ||
    Boolean(dateRange.to);

  const resetFilters = () => {
    setStatusFilter("all");
    setTypeFilter("all");
    setDateRange({ from: "", to: "" });
  };

  const statusTabs = STATUS_TABS.map((tab) => ({
    ...tab,
    count: counts[tab.key],
  }));

  const selectedLeave =
    selectedId === null
      ? null
      : leaveRequests.find((leave) => leave.leaveId === selectedId) || null;

  // --- actions available for a row (same rules as before) --------------------
  const getActionItems = (leave) => {
    const items = [
      {
        key: "view",
        label: "View details",
        icon: <Eye size={16} aria-hidden="true" />,
        onClick: () => setSelectedId(leave.leaveId),
      },
    ];
    const key = statusKeyOf(leave);
    if (key === "pending") {
      items.push({
        key: "cancel",
        label: "Cancel request",
        tone: "danger",
        icon: <Ban size={16} aria-hidden="true" />,
        onClick: () => setConfirm({ type: "cancel", leave }),
      });
    } else if (key === "approved" && canRevokeLeave(leave.leaveDates)) {
      items.push({
        key: "revoke",
        label: "Revoke leave",
        icon: <Undo2 size={16} aria-hidden="true" />,
        onClick: () => setConfirm({ type: "revoke", leave }),
      });
    }
    return items;
  };

  // --- table columns ------------------------------------------------------
  const columns = [
    {
      key: "leaveType",
      header: "Leave Type",
      sortAccessor: (r) => r.leaveTypeName,
      render: (r) => (
        <span className="my-leave__type">{r.leaveTypeName || "N/A"}</span>
      ),
    },
    {
      key: "comments",
      header: "Comments",
      sortAccessor: (r) => r.comments,
      render: (r) => <Truncate text={r.comments} fallback="No comment" />,
    },
    {
      key: "duration",
      header: "Duration",
      sortAccessor: (r) => formatDuration(r.leaveDuration),
      render: (r) => formatDuration(r.leaveDuration),
    },
    {
      key: "dates",
      header: "Dates",
      sortAccessor: (r) => firstLeaveTimestamp(r.leaveDates),
      render: (r) => (
        <span className="my-leave__dates">{formatDates(r.leaveDates)}</span>
      ),
    },
    {
      key: "days",
      header: "Days",
      align: "center",
      sortAccessor: (r) => r.noOfDays || 0,
      render: (r) => r.noOfDays || 0,
    },
    {
      key: "status",
      header: "Status",
      sortAccessor: (r) => normalizeStatus(r.leaveStatus).label,
      render: (r) => <StatusBadge status={r.leaveStatus} />,
    },
    {
      key: "fallbackEmployee",
      header: "Fallback Employee",
      sortAccessor: (r) => r.fallBackEmployeeName,
      render: (r) => <Truncate text={r.fallBackEmployeeName} fallback="N/A" />,
    },
    {
      key: "fallbackApproval",
      header: "Fallback Approval",
      sortAccessor: (r) => normalizeStatus(r.fallbackApprovalStatus).label,
      render: (r) => (
        <StatusBadge
          status={r.fallbackApprovalStatus}
          pendingLabel="Requested"
        />
      ),
    },
    {
      key: "responsibility",
      header: "Responsibility",
      sortAccessor: (r) => r.responsibility,
      render: (r) => <Truncate text={r.responsibility} fallback="N/A" />,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      interactive: true,
      render: (r) => (
        <ActionMenu
          label={`Actions for ${r.leaveTypeName || "leave"} request`}
          items={getActionItems(r)}
        />
      ),
    },
  ];

  // --- drawer footer actions ------------------------------------------------
  const drawerActions = selectedLeave
    ? getActionItems(selectedLeave).filter((item) => item.key !== "view")
    : [];

  return (
    <div className="my-leave">
      <div className="hr-summary-grid">
        <SummaryCard
          tone="warning"
          icon="pending_actions"
          label="Pending"
          value={error ? "–" : counts.pending}
          hint="Awaiting approval"
          loading={loading}
        />
        <SummaryCard
          tone="success"
          icon="task_alt"
          label="Approved"
          value={error ? "–" : counts.approved}
          hint="Approved requests"
          loading={loading}
        />
        <SummaryCard
          tone="danger"
          icon="cancel"
          label="Rejected / Cancelled"
          value={error ? "–" : counts.rejected + counts.cancelled}
          hint={`${counts.rejected} rejected · ${counts.cancelled} cancelled`}
          loading={loading}
        />
        <SummaryCard
          tone="primary"
          icon="calendar_month"
          label="Total Leave Days"
          value={error ? "–" : counts.totalDays}
          hint="Across requests in view"
          loading={loading}
        />
      </div>

      {error ? (
        <section className="hr-card">
          <ErrorState
            title="Couldn't load your leave requests"
            message={error}
            onRetry={handleRetry}
          />
        </section>
      ) : (
        <>
          <FilterToolbar
            tabs={
              <StatusTabs
                tabs={statusTabs}
                value={statusFilter}
                onChange={setStatusFilter}
                ariaLabel="Filter by status"
              />
            }
            onReset={resetFilters}
            resetDisabled={!filtersActive}
          >
            <Link to="/ess/apply-leave" className="hr-btn hr-btn--primary">
              <Plus size={16} aria-hidden="true" />
              Apply for Leave
            </Link>
            <SelectDropdown
              id="my-leave-type"
              label="Leave type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={leaveTypeOptions}
            />
            <DateRangeFilter
              from={dateRange.from}
              to={dateRange.to}
              onChange={setDateRange}
            />
          </FilterToolbar>

          <GeneralTable
            title="Leave Requests"
            columns={columns}
            data={visibleRows}
            rowKey={(row, index) => row.leaveId ?? index}
            loading={loading}
            emptyStateTitle="No leave requests found"
            emptyStateSubtitle={
              leaveRequests.length === 0
                ? "You haven't applied for any leave yet."
                : "Nothing matches the current filters or search."
            }
            onRowClick={(row) => setSelectedId(row.leaveId)}
            resetKey={JSON.stringify([
              statusFilter,
              typeFilter,
              dateRange.from,
              dateRange.to,
            ])}
            initialPageSize={10}
            pageSizeOptions={[10, 25, 50]}
          />
        </>
      )}

      <DetailsDrawer
        open={Boolean(selectedLeave)}
        onClose={() => setSelectedId(null)}
        title="Leave request"
        badge={
          selectedLeave ? (
            <StatusBadge status={selectedLeave.leaveStatus} />
          ) : null
        }
        footer={
          selectedLeave && (
            <>
              {drawerActions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`hr-btn ${
                    item.tone === "danger"
                      ? "hr-btn--danger"
                      : "hr-btn--secondary"
                  }`}
                  onClick={item.onClick}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                className="hr-btn hr-btn--secondary"
                onClick={() => setSelectedId(null)}
              >
                Close
              </button>
            </>
          )
        }
      >
        {selectedLeave && (
          <DetailList
            items={[
              {
                label: "Leave type",
                value: selectedLeave.leaveTypeName || "N/A",
              },
              {
                label: "Dates",
                value:
                  sortedLeaveDates(selectedLeave.leaveDates).length > 0 ? (
                    <ul className="hr-chip-list">
                      {sortedLeaveDates(selectedLeave.leaveDates).map(
                        (date) => (
                          <li key={date.getTime()}>{formatDateLong(date)}</li>
                        ),
                      )}
                    </ul>
                  ) : (
                    "N/A"
                  ),
              },
              {
                label: "Duration",
                value: formatDuration(selectedLeave.leaveDuration),
              },
              { label: "Days", value: selectedLeave.noOfDays || 0 },
              {
                label: "Comments",
                value: selectedLeave.comments || "No comment",
              },
              {
                label: "Fallback employee",
                value: selectedLeave.fallBackEmployeeName || "N/A",
              },
              {
                label: "Fallback approval",
                value: (
                  <StatusBadge
                    status={selectedLeave.fallbackApprovalStatus}
                    pendingLabel="Requested"
                  />
                ),
              },
              {
                label: "Responsibility",
                value: selectedLeave.responsibility || "N/A",
              },
            ]}
          />
        )}
      </DetailsDrawer>

      <ConfirmationDialog
        open={Boolean(confirm)}
        title={confirm ? CONFIRM_COPY[confirm.type].title : ""}
        message={confirm ? CONFIRM_COPY[confirm.type].message : ""}
        confirmLabel={confirm ? CONFIRM_COPY[confirm.type].confirmLabel : ""}
        busy={actionBusy}
        onConfirm={runConfirmedAction}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default MyLeave;
